import { FeatureLabel } from '@/components/marketing/info-tip';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, X, ArrowRightLeft } from 'lucide-react';
import { MarketingShell } from '@/components/marketing/warm/marketing-shell';
import { VerdictBlock } from '@/components/compare/verdict-block';
import { FeatureMatrix } from '@/components/compare/feature-matrix';
import { PricingCalculator } from '@/components/compare/pricing-calculator';
import { CompareFaq } from '@/components/compare/compare-faq';
import { CompareCta } from '@/components/compare/compare-cta';
import { ALL_COMPETITORS, type CompetitorData } from '@/routes/compare/data';
import noindexConfig from '@/lib/noindex.json';
import {
  setDocumentMeta, setCanonical, setOgImage, injectJsonLdList,
  buildWebPageSchema, buildItemListSchema, buildFaqPageSchema,
  buildBreadcrumbSchema, buildSoftwareApplicationSchema, buildOfferSchema,
} from '@/lib/seo';

interface ComparisonPageProps {
  data: CompetitorData;
}

export function ComparisonPage({ data }: ComparisonPageProps) {
  useEffect(() => {
    const title = data.metaTitle ?? `Kora vs ${data.name} — Pricing & Features`;
    const description = data.metaDescription ?? `Kora vs ${data.name}: side-by-side pricing and features. Kora is pay per ticket ($0.10, unlimited members, no seats). See which fits your team.`;
    const path = `/compare/${data.slug}`;
    setDocumentMeta(title, description);
    setCanonical(path);
    setOgImage('/assets/og-compare.png');

    const cleanup = injectJsonLdList([
      buildWebPageSchema(path, title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app' },
        { name: 'Compare', url: 'https://usekora.app/compare' },
        { name: `Kora vs ${data.name}`, url: `https://usekora.app${path}` },
      ]),
      buildItemListSchema([
        { name: 'Kora', url: 'https://usekora.app', description: 'AI-powered issue tracking for modern ops teams' },
        { name: data.name, url: `https://usekora.app${path}`, description: data.tagline },
      ]),
      buildFaqPageSchema(data.faqs),
      buildSoftwareApplicationSchema(
        'Kora',
        'AI helpdesk and issue tracker priced per ticket: $0.10 per ticket, unlimited members, no monthly plans.',
        [
          buildOfferSchema(0, 'USD', 'Start free: 1,100 free credits on signup, no card required, unlimited members'),
          buildOfferSchema(5, 'USD', 'Pay as you go — $1 = 10 credits, 1 credit per ticket created; first recharge +30% bonus credits'),
        ],
      ),
    ]);

    return cleanup;
  }, [data.name, data.slug, data.tagline, data.faqs]);

  const otherComparisons = ALL_COMPETITORS.filter((c) => c.slug !== data.slug && noindexConfig.compareIndexed.includes(c.slug));

  return (
    <MarketingShell>
      <div className="docs-warm pt-12">

      {/* Verdict block */}
      <VerdictBlock data={data} />

      {/* At-a-glance summary */}
      <section className="py-12 md:py-16 border-t border-zinc-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-2xl md:text-3xl text-zinc-900 mb-6 text-center">
            At a glance
          </h2>
          <div className="rounded-2xl border border-zinc-200 overflow-hidden">
            <div className="grid grid-cols-12 px-4 py-3 bg-zinc-50 border-b border-zinc-200">
              <div className="col-span-6 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Feature
              </div>
              <div className="col-span-3 text-center text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Kora
              </div>
              <div className="col-span-3 text-center text-xs font-semibold uppercase tracking-wide text-zinc-500">
                {data.name}
              </div>
            </div>
            {data.atAGlance.map((row, i) => (
              <div
                key={row.label}
                className={`grid grid-cols-12 px-4 py-3 items-center ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/50'
                  }`}
              >
                <div className="col-span-6 text-sm text-zinc-700"><FeatureLabel label={row.label} /></div>
                <div className="col-span-3 text-center text-xs text-zinc-900">
                  {row.kora}
                </div>
                <div className="col-span-3 text-center text-xs text-zinc-900">
                  {row.competitor}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing calculator */}
      <PricingCalculator data={data} />

      {/* Feature matrix */}
      <FeatureMatrix data={data} />
      <p className="max-w-4xl mx-auto px-4 sm:px-6 -mt-8 mb-10 text-xs text-zinc-400">
        {data.name} prices are list prices from the vendor's pricing page as last checked (October 2026); where a price is marked
        not re-verified we have not rechecked it. Feature rows summarize vendor pages and may be out of date. Prices and features
        change, so confirm with {data.name} before you buy. Spot an error? Email founder@usekora.app and we will fix it.
      </p>

      {/* Where each wins */}
      <section className="py-16 md:py-20 border-t border-zinc-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-2xl md:text-3xl text-zinc-900 mb-2 text-center">
            Where each one wins
          </h2>
          <p className="text-sm text-zinc-500 mb-10 text-center">
            No tool wins everything. Here's the honest split.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Kora wins */}
            <div className="rounded-2xl border border-zinc-200 bg-white p-6">
              <div className="flex items-center gap-2 mb-4">
                <img width={20} height={20} src="/assets/logo_square.svg" alt="Kora logo" className="size-5" />
                <span className="text-sm font-semibold text-zinc-900">
                  Where Kora wins
                </span>
              </div>
              <ul className="space-y-3">
                {data.koraWins.map((w) => (
                  <li key={w} className="flex items-start gap-2 text-sm text-zinc-600">
                    <Check className="size-4 text-green-500 shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Competitor wins */}
            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="size-5 rounded bg-zinc-200 flex items-center justify-center text-[10px] font-bold text-zinc-600">
                  {data.name[0]}
                </div>
                <span className="text-sm font-semibold text-zinc-900">
                  Where {data.name} wins
                </span>
              </div>
              <ul className="space-y-3">
                {data.competitorWins.map((w) => (
                  <li key={w} className="flex items-start gap-2 text-sm text-zinc-600">
                    <Check className="size-4 text-zinc-400 shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Switching section */}
      <section className="py-16 md:py-20 border-t border-zinc-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs font-medium text-zinc-600 mb-4">
              <ArrowRightLeft className="size-3" /> Migration
            </div>
            <h2 className="font-display text-2xl md:text-3xl text-zinc-900 mb-2">
              Why teams switch from {data.name}
            </h2>
            <p className="text-sm text-zinc-500">
              Common reasons teams move to Kora.
            </p>
          </div>
          <div className="space-y-3 mb-8">
            {data.switchingReasons.map((reason) => (
              <div
                key={reason}
                className="flex items-start gap-3 rounded-xl border border-zinc-200 bg-white p-4"
              >
                <X className="size-4 text-red-400 shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-600">{reason}</span>
              </div>
            ))}
          </div>
          <div className="rounded-2xl bg-zinc-50 border border-zinc-200 p-6">
            <div className="text-sm font-medium text-zinc-900 mb-4">
              How to migrate
            </div>
            <ol className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-zinc-600">
                <span className="size-5 rounded-full bg-zinc-900 text-white text-xs font-bold flex items-center justify-center shrink-0">
                  1
                </span>
                Export your tickets from {data.name} as CSV
              </li>
              <li className="flex items-start gap-3 text-sm text-zinc-600">
                <span className="size-5 rounded-full bg-zinc-900 text-white text-xs font-bold flex items-center justify-center shrink-0">
                  2
                </span>
                Paste raw descriptions into Kora — AI structures them into clean tickets
              </li>
              <li className="flex items-start gap-3 text-sm text-zinc-600">
                <span className="size-5 rounded-full bg-zinc-900 text-white text-xs font-bold flex items-center justify-center shrink-0">
                  3
                </span>
                Or use the Public API to import tickets, comments, and attachments programmatically
              </li>
              <li className="flex items-start gap-3 text-sm text-zinc-600">
                <span className="size-5 rounded-full bg-zinc-900 text-white text-xs font-bold flex items-center justify-center shrink-0">
                  4
                </span>
                Invite your team — unlimited members, no seat fees
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* Kora AI pricing, only where the competitor's AI price is sourced */}
      {(data.slug === 'kora-vs-zendesk' || data.slug === 'kora-vs-intercom') && (
        <section className="py-12 md:py-16 border-t border-zinc-100">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="font-display text-2xl md:text-3xl text-zinc-900 mb-3">How Kora AI pricing compares</h2>
            <p className="text-sm md:text-base text-zinc-600 leading-relaxed">
              Kora AI charges $0.10 per drafted reply and $0.30 per confirmed resolution, with no seats.{' '}
              {data.slug === 'kora-vs-zendesk'
                ? 'Zendesk charges US$2.00 per automated resolution pay-as-you-go (zendesk.com/pricing, checked 28 Oct 2026).'
                : 'Intercom Fin charges $0.99 per outcome, plus $19 per helpdesk seat if used with Intercom\'s helpdesk (fin.ai/pricing, checked 28 Oct 2026).'}{' '}
              Vendors define a resolution differently, so compare what is counted, not only the rate.{' '}
              <Link to="/ai-helpdesk-pay-per-resolution" className="underline hover:text-zinc-900">See how Kora AI billing works</Link>.
            </p>
          </div>
        </section>
      )}

      {/* FAQ */}
      <CompareFaq faqs={data.faqs} />

      {/* Related pages for the two biggest comparisons */}
      {(data.slug === 'kora-vs-zendesk' || data.slug === 'kora-vs-freshdesk') && (
        <section className="pt-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <Link to={data.slug === 'kora-vs-zendesk' ? '/zendesk-alternative' : '/freshdesk-alternative'} className="font-medium text-zinc-700 underline hover:text-zinc-900">
              {data.slug === 'kora-vs-zendesk' ? 'Zendesk alternative' : 'Freshdesk alternative'}
            </Link>
            <Link to="/customer-portal-software" className="font-medium text-zinc-700 underline hover:text-zinc-900">Customer portal software</Link>
          </div>
        </section>
      )}

      {/* Other comparisons */}
      <section className="py-12 border-t border-zinc-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h3 className="text-sm font-semibold text-zinc-900 mb-4 text-center">
            Other comparisons
          </h3>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {otherComparisons.map((c) => (
              <Link
                key={c.slug}
                to={`/compare/${c.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-3.5 py-1.5 text-xs font-medium text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 transition-colors"
              >
                Kora vs {c.name} <ArrowRight className="size-3" />
              </Link>
            ))}
            <Link
              to="/compare"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <ArrowLeft className="size-3" /> All comparisons
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <CompareCta />
      </div>
    </MarketingShell>
  );
}
